import dayjs from 'dayjs'
import { Dispatch, SetStateAction, useState } from 'react'
import { t, Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'

import { Box, Button, Chip, Drawer, IconButton, Stack, Typography, useMediaQuery, useTheme } from '@mui/material'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'

import { Planning } from '@/core/models/planning.model'
import { SetQueryParam } from '@/hooks/useQueryParam'
import { EditPlanningDialog } from '@/shared/plannings'
import { getPlanningStatus } from '@/shared/plannings/lib/planningSelection'

import { CreateShiftTemplateDialog } from './CreateShiftTemplateDialog'
import { PlanningSelector } from './PlanningSelector'
import { StatisticCards } from './StatisticCards'
import { ViewModeToggler } from './ViewModeToggler'
import { PlanningViewMode } from '../model/types'

export function Sidebar({
  plannings,
  planningViewMode,
  setPlanningViewMode,
  setPlanningId,
  selectedPlanning,
}: {
  plannings: Planning[]
  planningViewMode: PlanningViewMode
  setPlanningViewMode: Dispatch<SetStateAction<PlanningViewMode>>
  setPlanningId: SetQueryParam<string>
  selectedPlanning?: Planning
}) {
  useLingui()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [selectorOpen, setSelectorOpen] = useState(false)

  if (isMobile) {
    return (
      <Box
        data-testid="planning-sidebar"
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <Stack
          data-testid="planning-mobile-toolbar"
          direction="row"
          alignItems="center"
          gap={0.75}
          sx={{
            p: 1,
            overflow: 'hidden',
          }}
        >
          <Button
            data-testid="planning-mobile-change-planning"
            endIcon={<KeyboardArrowDownIcon />}
            onClick={() => setSelectorOpen(true)}
            size="small"
            sx={{
              alignItems: 'stretch',
              flexGrow: 1,
              justifyContent: 'space-between',
              minWidth: 0,
              px: 1,
              textAlign: 'left',
              textTransform: 'none',
            }}
            variant="outlined"
          >
            <SelectedPlanningSummary planning={selectedPlanning} />
          </Button>
          {selectedPlanning && (
            <Stack direction="row" alignItems="center" gap={0.25} sx={{ flexShrink: 0 }}>
              <ViewModeToggler compact viewMode={planningViewMode} onChange={setPlanningViewMode} toggleType="calendar-view" />
              <CreateShiftTemplateDialog planningId={selectedPlanning._id} triggerVariant="icon" />
              <EditPlanningDialog planning={selectedPlanning} />
            </Stack>
          )}
        </Stack>

        <Drawer
          anchor="bottom"
          open={selectorOpen}
          onClose={() => setSelectorOpen(false)}
          PaperProps={{
            sx: {
              borderTopLeftRadius: 2,
              borderTopRightRadius: 2,
              maxHeight: '82dvh',
            },
          }}
        >
          <Box data-testid="planning-selector-drawer" sx={{ p: 1.5, overflow: 'auto' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle1" fontWeight={700}>
                <Trans>Plannings</Trans>
              </Typography>
              <IconButton aria-label={t`Close`} onClick={() => setSelectorOpen(false)} size="small">
                <CloseRoundedIcon fontSize="small" />
              </IconButton>
            </Stack>
            <PlanningSelector
              plannings={plannings}
              selectedPlanning={selectedPlanning}
              onSelectPlanning={(planning) => {
                setPlanningId(planning._id)
                setSelectorOpen(false)
              }}
              showSelectedPlanningActions={false}
            />
          </Box>
        </Drawer>
      </Box>
    )
  }

  return (
    <Box
      data-testid="planning-sidebar"
      sx={{
        borderBottom: isMobile ? 1 : 0,
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      {/* Stats cards — hidden on mobile to reduce clutter (the calendar already shows shift stats). */}
      {!isMobile && <StatisticCards plannings={plannings} />}

      {/* Planning selector — capped on mobile so the (potentially long) list scrolls
          internally instead of pushing the calendar far down the page. */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', maxHeight: isMobile ? '45vh' : undefined }}>
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
          <Trans>Plannings</Trans>
        </Typography>
        <PlanningSelector
          plannings={plannings}
          selectedPlanning={selectedPlanning}
          onSelectPlanning={(planning) => setPlanningId(planning._id)}
          calendarViewMode={planningViewMode}
          onCalendarViewModeChange={setPlanningViewMode}
        />
      </Box>
    </Box>
  )
}

function SelectedPlanningSummary({ planning }: { planning?: Planning }) {
  if (!planning) {
    return (
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" fontWeight={700} noWrap>
          <Trans>Plannings</Trans>
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          <Trans>No Planning Selected</Trans>
        </Typography>
      </Box>
    )
  }

  const status = getPlanningStatus(planning)

  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="body2" fontWeight={700} noWrap>
        {planning.name}
      </Typography>
      <Stack direction="row" alignItems="center" gap={0.5} sx={{ minWidth: 0 }}>
        <Chip label={<PlanningStatusLabel status={status} />} color={getStatusColor(status)} size="small" variant="outlined" sx={{ height: 20 }} />
        <Typography variant="caption" color="text.secondary" noWrap>
          {dayjs(planning.startDate).format('MMM D')} - {dayjs(planning.endDate).format('MMM D, YYYY')}
        </Typography>
      </Stack>
    </Box>
  )
}

function PlanningStatusLabel({ status }: { status: ReturnType<typeof getPlanningStatus> }) {
  switch (status) {
    case 'active':
      return <Trans>Active</Trans>
    case 'upcoming':
      return <Trans>Upcoming</Trans>
    case 'past':
      return <Trans>Past</Trans>
  }
}

function getStatusColor(status: ReturnType<typeof getPlanningStatus>): 'success' | 'info' | 'default' {
  if (status === 'active') return 'success'
  if (status === 'upcoming') return 'info'
  return 'default'
}
